import Card from 'tio-ui/components/card';
import Heading from 'tio-ui/components/heading';
import IconBadge from 'tio-ui/components/icon-badge';
import type { IconBadgeColor } from 'tio-ui/components/icon-badge';
import type { ComponentLike } from '@glint/template';
import type { TOC } from '@ember/component/template-only';

export interface LandingInfoCardSignature {
  Args: {
    /** Decorative Phosphor icon shown in the card's tinted badge. */
    icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    /**
     * Colour intent for the badge. The decorative palette collapses onto the
     * six canonical recipes (neutral / success / info / brand / danger /
     * warning), so pick the name that carries the intent.
     */
    iconColor?: IconBadgeColor;
    /** Translated card title; renders as the panel-level h3. */
    title: string;
  };
  Blocks: {
    /** Card body — copy, grids of tiles, steps, or a call to action. */
    default: [];
  };
  Element: HTMLDivElement;
}

/**
 * Info card for a tab leaf of a landing page: a badged icon, a panel-level
 * title, and whatever body the leaf needs.
 *
 * This is the card treatment the Tuition Assistance dashboard ships for
 * content inside a zone, so a leaf renders as a set of peer cards under the
 * shell's one section heading rather than as a stack of nested zones. The
 * title is an h3 because the card always sits under that h2.
 */
const LandingInfoCard: TOC<LandingInfoCardSignature> = <template>
  <Card data-test-info-card class="tio-col-loose" ...attributes>
    <div class="tio-row-tight">
      <IconBadge @icon={{@icon}} @color={{@iconColor}} @size="lg" />
      <Heading data-test-info-card-title @level={{3}} @role="panel-title">
        {{@title}}
      </Heading>
    </div>
    {{yield}}
  </Card>
</template>;

export default LandingInfoCard;
